import React, { useState, useEffect, useMemo } from 'react';
import { getAllCallsFromDb, getClinics } from '../../api';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Определяем темную тему для MUI компонентов
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',
    },
    background: {
      default: '#111827',
      paper: '#1f2937',
    },
  },
});

const ChartContainer = ({ title, children }) => (
  <div className="bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-700">
    <h3 className="text-lg font-semibold mb-4 text-white text-center">{title}</h3>
    {children}
  </div>
);

const DashboardPage = () => {
  const [callsData, setCallsData] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Состояние фильтров
  const [filters, setFilters] = useState({
    clientId: '',
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0].split('-').reverse().join('.'), // 7 дней назад
    endDate: new Date().toISOString().split('T')[0].split('-').reverse().join('.'),
    isEffective: 'all',
  });

  // Загрузка списка клиник при монтаже
  useEffect(() => {
    const fetchClinicsList = async () => {
      try {
        const data = await getClinics();
        setClinics(data || []);
        if (data && data.length > 0 && !filters.clientId) {
          setFilters(prev => ({ ...prev, clientId: data[0].client_id }));
        }
      } catch (err) {
        console.error('Ошибка при загрузке клиник:', err);
      }
    };
    fetchClinicsList();
  }, []);

  const fetchData = async () => {
    if (!filters.clientId) return;

    setLoading(true);
    setError(null);
    try {
      const apiFilters = {};
      if (filters.isEffective === 'true') apiFilters.is_effective = true;
      if (filters.isEffective === 'false') apiFilters.is_effective = false;

      const response = await getAllCallsFromDb(
        filters.clientId,
        200, // limit
        0,   // skip
        filters.startDate,
        filters.endDate,
        apiFilters
      );

      if (response && response.data && response.data.calls) {
        // Дополнительная фильтрация "другое" на фронтенде (ТЗ)
        const filteredCalls = response.data.calls.filter(call => {
          const type = (call.metrics?.call_type_classification || '').toLowerCase();
          return !['другое', 'other', 'undefined', ''].includes(type);
        });
        setCallsData(filteredCalls);
      } else {
        setCallsData([]);
      }
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.clientId, filters.startDate, filters.endDate, filters.isEffective]);

  // Обработчики фильтров
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Процессинг данных для графиков
  const chartData = useMemo(() => {
    if (!callsData.length) return { byDay: [], avgScore: [], types: [], satisfaction: [], conversion: [] };

    // Звонки по дням и средний балл
    const dailyData = callsData.reduce((acc, call) => {
      const day = call.created_date_for_filtering || 'Unknown';
      const score = parseFloat(call.metrics?.overall_score);

      if (!acc[day]) acc[day] = { day, calls: 0, totalScore: 0, scoreCount: 0 };
      acc[day].calls += 1;
      if (!isNaN(score)) {
        acc[day].totalScore += score;
        acc[day].scoreCount += 1;
      }
      return acc;
    }, {});

    const sortedDaily = Object.values(dailyData).sort((a, b) => new Date(a.day) - new Date(b.day));

    // Типы звонков
    const typeCounts = callsData.reduce((acc, call) => {
      const type = call.metrics?.call_type_classification || 'Не определен';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Удовлетворенность
    const satCounts = callsData.reduce((acc, call) => {
      const sat = call.metrics?.customer_satisfaction?.toLowerCase() || 'unknown';
      acc[sat] = (acc[sat] || 0) + 1;
      return acc;
    }, {});

    // Конверсия
    const convCounts = callsData.reduce((acc, call) => {
      const isConv = call.metrics?.conversion === true;
      acc[isConv ? 'yes' : 'no'] += 1;
      return acc;
    }, { yes: 0, no: 0 });

    return {
      byDay: sortedDaily.map(d => ({ day: d.day, calls: d.calls })),
      avgScore: sortedDaily.filter(d => d.scoreCount > 0).map(d => ({ day: d.day, averageScore: parseFloat((d.totalScore / d.scoreCount).toFixed(1)) })),
      types: Object.entries(typeCounts).map(([label, value], id) => ({ id, value, label })),
      satisfaction: [
        { id: 0, value: satCounts.high || 0, label: 'Доволен', color: '#10b981' },
        { id: 1, value: satCounts.medium || 0, label: 'Нейтрально', color: '#f59e0b' },
        { id: 2, value: satCounts.low || 0, label: 'Недоволен', color: '#ef4444' },
      ].filter(i => i.value > 0),
      conversion: [
        { id: 0, value: convCounts.yes, label: 'Запись', color: '#10b981' },
        { id: 1, value: convCounts.no, label: 'Нет записи', color: '#ef4444' },
      ].filter(i => i.value > 0)
    };
  }, [callsData]);

  if (error) return <div className="text-center py-10 text-red-500">{error}</div>;

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <div className="space-y-6 pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h2 className="text-3xl font-bold text-white">Дашборд эффективности</h2>

          {/* Панель фильтров */}
          <div className="flex flex-wrap items-center gap-3 bg-gray-800 p-3 rounded-xl border border-gray-700">
            <select
              name="clientId"
              value={filters.clientId}
              onChange={handleFilterChange}
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 outline-none border border-gray-600 focus:border-blue-500"
            >
              <option value="">Выберите клинику</option>
              {clinics.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </select>

            <input
              type="text"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              placeholder="ДД.ММ.ГГГГ"
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 w-32 outline-none border border-gray-600 focus:border-blue-500"
            />
            <span className="text-gray-500">-</span>
            <input
              type="text"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              placeholder="ДД.ММ.ГГГГ"
              className="bg-gray-700 text-white text-sm rounded-lg px-3 py-2 w-32 outline-none border border-gray-600 focus:border-blue-500"
            />


            <button
              onClick={fetchData}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Применить
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="text-gray-400">Загрузка данных...</p>
          </div>
        ) : (
          <>
            {/* Графики */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ChartContainer title="Звонки по дням">
                <BarChart
                  dataset={chartData.byDay}
                  xAxis={[{ scaleType: 'band', dataKey: 'day', tickLabelStyle: { angle: -45, textAnchor: 'end', fontSize: 10 } }]}
                  series={[{ dataKey: 'calls', color: '#3b82f6' }]}
                  height={240}
                  margin={{ top: 10, bottom: 60, left: 40, right: 10 }}
                />
              </ChartContainer>

              <ChartContainer title="Средний балл">
                <BarChart
                  dataset={chartData.avgScore}
                  xAxis={[{ scaleType: 'band', dataKey: 'day', tickLabelStyle: { angle: -45, textAnchor: 'end', fontSize: 10 } }]}
                  series={[{ dataKey: 'averageScore', color: '#10b981' }]}
                  height={240}
                  margin={{ top: 10, bottom: 60, left: 40, right: 10 }}
                />
              </ChartContainer>

              <ChartContainer title="Конверсия">
                <PieChart
                  series={[{ data: chartData.conversion, innerRadius: 40, paddingAngle: 5, cornerRadius: 5 }]}
                  height={240}
                />
              </ChartContainer>

              <ChartContainer title="Типы звонков">
                <PieChart
                  series={[{ data: chartData.types, innerRadius: 40, paddingAngle: 2 }]}
                  height={240}
                />
              </ChartContainer>

              <ChartContainer title="Удовлетворенность">
                <PieChart
                  series={[{ data: chartData.satisfaction, innerRadius: 40 }]}
                  height={240}
                />
              </ChartContainer>
            </div>

            {/* Таблица звонков */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h3 className="text-xl font-semibold text-white">Рекомендация таблица</h3>

                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-500 uppercase font-bold tracking-widest">Фильтр:</span>
                  <select
                    name="isEffective"
                    value={filters.isEffective}
                    onChange={handleFilterChange}
                    className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 outline-none border border-gray-700 focus:border-blue-500 min-w-[180px]"
                  >
                    <option value="all">Все звонки</option>
                    <option value="true">✅ Эффективные</option>
                    <option value="false">❌ Неэффективные</option>
                  </select>
                  <span className="text-sm text-gray-400 bg-gray-900 px-3 py-1 rounded-full border border-gray-700">
                    Найдено: {callsData.length}
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-900/50 text-gray-400 text-xs uppercase tracking-wider">
                      <th className="p-4 font-medium">Дата</th>
                      <th className="p-4 font-medium">Администратор</th>
                      <th className="p-4 font-medium text-center">Эффективность</th>
                      <th className="p-4 font-medium">Критерии</th>
                      <th className="p-4 font-medium text-center">Общий балл</th>
                      <th className="p-4 font-medium text-center">Текст</th>
                      <th className="p-4 font-medium text-center">Запись</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {callsData.map((call) => (
                      <tr key={call._id} className="hover:bg-gray-700/30 transition-colors border-b border-gray-700/50">
                        <td className="p-4 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(call.created_at * 1000).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="p-4 text-sm text-white font-medium">
                          {call.administrator || '—'}
                        </td>
                        <td className="p-4 text-center">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-tighter ${(call.effectiveness_status === 'эффективный' || call.metrics?.is_effective)
                            ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}>
                            {call.effectiveness_status || (call.metrics?.is_effective ? 'Эффективен' : 'Не эффективен')}
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-400 max-w-[200px] truncate group relative">
                          {call.criteria_with_scores || (call.effectiveness_criteria || call.metrics?.matched_criteria)?.join(', ') || '—'}
                          {(call.criteria_with_scores || call.effectiveness_criteria?.length > 0 || call.metrics?.matched_criteria?.length > 0) && (
                            <div className="absolute z-10 hidden group-hover:block bg-gray-900 text-white p-2 rounded shadow-xl border border-gray-700 -bottom-8 left-0 max-w-xs whitespace-normal">
                              {call.criteria_with_scores || (call.effectiveness_criteria || call.metrics?.matched_criteria).join(', ')}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {call.overall_average_score != null ? (
                            <span className="text-sm font-bold text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                              {typeof call.overall_average_score === 'number'
                                ? call.overall_average_score.toFixed(1)
                                : call.overall_average_score}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {call._id && (
                            <a
                              href={`/api/transcriptions/${call._id}/download`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-400 hover:text-blue-300 transform hover:scale-110 transition-all inline-block"
                              title="Скачать транскрипцию"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </a>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {call.recording_url ? (
                            <a
                              href={call.recording_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-purple-400 hover:text-purple-300 transform hover:scale-110 transition-all inline-block"
                              title="Прослушать запись"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                              </svg>
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                    {callsData.length === 0 && (
                      <tr>
                        <td colSpan="7" className="p-12 text-center text-gray-500">
                          Звонки не найдены за выбранный период
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

export default DashboardPage;
